import { useEffect, useRef } from 'react';
import { Task } from '../types';

export const useReminders = (tasks: Task[], onClearReminder?: (taskId: string) => void) => {
    const notifiedReminders = useRef<Set<string>>(new Set());

    useEffect(() => {
        // Request notification permission on mount
        if ('Notification' in window && Notification.permission === 'default') {
            Notification.requestPermission();
        }
    }, []);

    useEffect(() => {
        const checkReminders = () => {
            const now = new Date();

            tasks.forEach((task) => {
                if (task.reminderTime && !task.completed) {
                    const reminderTime = new Date(task.reminderTime);
                    const reminderKey = `${task.id}-${task.reminderTime}`;

                    // Check if reminder time has passed and hasn't been notified yet
                    if (reminderTime <= now && !notifiedReminders.current.has(reminderKey)) {
                        // Send notification with error handling
                        try {
                            if ('Notification' in window && Notification.permission === 'granted') {
                                new Notification('Task Reminder', {
                                    body: task.title || task.task || 'Task reminder',
                                    icon: '/favicon.ico',
                                    tag: reminderKey,
                                });
                            }
                        } catch (error) {
                            console.error('Failed to show notification:', error);
                        }

                        // Mark as notified
                        notifiedReminders.current.add(reminderKey);

                        // Clear the reminder from the task
                        if (onClearReminder) {
                            onClearReminder(task.id);
                        }
                    }
                }
            });
        };

        // Check immediately
        checkReminders();

        // Check every 30 seconds
        const interval = setInterval(checkReminders, 30000);

        return () => clearInterval(interval);
    }, [tasks, onClearReminder]);

    // Reset notified reminders when tasks change significantly
    useEffect(() => {
        const activeReminderKeys = new Set(
            tasks
                .filter((task) => task.reminderTime && !task.completed)
                .map((task) => `${task.id}-${task.reminderTime}`)
        );

        // Clean up old reminders that are no longer relevant
        notifiedReminders.current = new Set(
            [...notifiedReminders.current].filter((key) => activeReminderKeys.has(key))
        );
    }, [tasks]);
};
